import { watch } from 'vue'
import type { FetchRequest } from 'ofetch'
import { resetAccountScopedState } from '~/composables/resetAccountScopedState'
import { revokeStripeLinkTrust } from '~/composables/revokeStripeLinkTrust'
import { useLogger } from '~/composables/useLogger'
import { useUserAccess } from '~/composables/useUserAccess'
import { identityGeneration } from '~/utils/identityGeneration'
import { invalidateRejectedSanctumToken } from '~/utils/invalidateRejectedSanctumToken'
import { normalizeIdentity } from '~/utils/normalizeIdentity'
import { sanctumTokenStorage } from '~/utils/sanctumTokenStorage'

const logger = useLogger('IdentityResetPlugin')

const REJECTED_STATUS = 401

const rejectedBearerToken = (headers: Headers): string | undefined => {
  const header = headers.get('Authorization')
  if (header === null) return undefined
  const [scheme, token] = header.split(' ')
  if (scheme !== 'Bearer' || token === undefined || token.length === 0) return undefined
  return token
}

const requestPath = (request: FetchRequest): string =>
  typeof request === 'string' ? request : request.url

// Covers every path that nulls identity -- explicit logout and the vendor's 401 interceptor -- not just route reconciliation.
export default defineNuxtPlugin((nuxtApp) => {
  const user = useSanctumUser()

  // `sanctum:response` runs for every response, including the initial identity load's
  // `ignoreResponseError` 401 that never reaches an error interceptor at all.
  nuxtApp.hook('sanctum:response', async (_app, context) => {
    if (context.response?.status !== REJECTED_STATUS) return
    // The vendor's 401 handler is a no-op once `user` is already null, so the rejected
    // bearer is read off the failed request rather than from the current identity.
    const rejectedToken = rejectedBearerToken(context.options.headers)
    if (rejectedToken === undefined) return
    // A broken storage read must not suppress the one signal that matters more: assume
    // relevance rather than silently treating a late, stale rejection as the current session's.
    let rejectedTokenWasCurrent = true
    try {
      rejectedTokenWasCurrent = invalidateRejectedSanctumToken(rejectedToken)
      if (rejectedTokenWasCurrent) identityGeneration.recordRejectedCredential(context)
      logger.warn('rejectedCredentialInvalidated', { path: requestPath(context.request) })
    } catch (error: unknown) {
      logger.error('rejectedCredentialInvalidated', { error })
    }
    if (!rejectedTokenWasCurrent) return
    // Bumped here, not only inside resetAccountScopedState below: a save already in flight
    // under the rejected credential must never read as still-current once this hook returns.
    identityGeneration.bump()
    await revokeStripeLinkTrust(null)
  })

  watch(
    user,
    (rawNewUser, rawOldUser) => {
      if (rawOldUser == null) return
      const oldIdentity = rawOldUser == null ? null : normalizeIdentity(rawOldUser, logger)
      const newIdentity = rawNewUser == null ? null : normalizeIdentity(rawNewUser, logger)
      const samePrincipal =
        oldIdentity !== null && newIdentity !== null && oldIdentity.id === newIdentity.id
      if (samePrincipal) return

      const resetPromise = resetAccountScopedState(nuxtApp, {
        clearToken: newIdentity === null,
        departingIdentityId: oldIdentity?.id ?? null
      })
      void identityGeneration
        .resolveFor(() => resetPromise)
        .then(async (outcome) => {
          const result = outcome.value
          if (!result.cleared) {
            logger.error('resetOnIdentityClearedIncomplete', { failedSteps: result.failedSteps })
            if (outcome.status === 'current') {
              try {
                await sanctumTokenStorage.set(nuxtApp, undefined)
              } catch (tokenError: unknown) {
                // Only the error's name/kind -- a raw storage exception can carry stored values.
                logger.error('resetOnIdentityClearedTokenClearFailed', {
                  errorName: tokenError instanceof Error ? tokenError.name : 'UnknownError'
                })
              }
              user.value = null
            }
            return
          }
          if (newIdentity !== null && outcome.status === 'current') {
            void useUserAccess().fetchUserAccess(true)
          }
        })
        .catch((error: unknown) => {
          logger.error('resetOnIdentityCleared', {
            errorName: error instanceof Error ? error.name : 'UnknownError'
          })
        })
    },
    { immediate: false }
  )
})
